import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { providerSettlementsApi, actionMessage } from '../../services/providerSettlementsApi';
import { deliveryConfigApi } from '../../services/deliveryConfigApi';
import { RecoveryProvider } from '../../types/delivery';
import {
  PROVIDER_SETTLEMENT_STATUSES,
  ProviderSettlement,
  ProviderSettlementStatus,
  UnsettledProvider,
  dubaiToday,
  formatPeriod,
  formatTripDate,
  monthLabel,
  monthRange,
  previousMonth,
} from '../../types/providerSettlements';
import ProviderSettlementDetail, { ProviderSettlementStatusBadge, SettlementAmount } from './ProviderSettlementDetail';
import Money, { formatAed } from '../finance/Money';
import InfoTip from '../finance/InfoTip';
import UnsettledTripsPanel from './UnsettledTripsPanel';

/**
 * Spec 053 Flow F7 — Settlements → **Providers**.
 *
 * Garagat paying its recovery providers for the trips they drove. Since 2026-09-27 (Maher): pick a
 * provider and a period — a month, or every unpaid trip up to a date — see how many orders and trips
 * it covers with each trip linked to its order, then **Mark as paid** in one step (amount mandatory,
 * invoice optional and attachable later). Drafts prepared before that still open, pay and void from
 * the list below. Nothing here is visible to a partner or a customer, and none of it touches the
 * customer's delivery fee, which was fixed at booking (AC-41b).
 *
 * The provider picker reads the registry from the **orders** service (`/api/admin/delivery/**`,
 * screen `finance`) while this tab itself is screen `settlements`. An admin whose role has
 * settlements but not finance therefore gets a 403 on the registry alone: the list, the filters and
 * every action below keep working, and only the provider picker says why it cannot offer one. That is
 * deliberate — degrading the whole tab because of the picker would be the wrong failure.
 *
 * #634 — the picker is NOT just the active registry. Merchant-settlement also answers which
 * providers still have unsettled trips (`/unsettled-providers`, read from the trips), and every one
 * of those is offered too: a provider made inactive, or retired (which drops it from the registry),
 * is still owed for the trips it drove, and prepare never required an active provider. That list is
 * on the `settlements` screen, so it also keeps the picker working when the registry is forbidden.
 *
 * #1254 — Zofeur is not in the registry any more (it is set up on the Zofeur card), but its trips
 * are paid here, so the picker asks for the card's provider row too (`includeZofeur`).
 */
export default function ProvidersTab() {
  const auth = useAuth();

  const [settlements, setSettlements] = useState<ProviderSettlement[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [providers, setProviders] = useState<RecoveryProvider[]>([]);
  const [registryError, setRegistryError] = useState<string | null>(null);
  const [owed, setOwed] = useState<UnsettledProvider[]>([]);

  const [filterProvider, setFilterProvider] = useState('');
  const [filterStatus, setFilterStatus] = useState<ProviderSettlementStatus | ''>('');

  const [payProvider, setPayProvider] = useState('');
  const [periodMode, setPeriodMode] = useState<'month' | 'until'>('month');
  const [month, setMonth] = useState(() => previousMonth(dubaiToday()));
  const [until, setUntil] = useState(() => dubaiToday());
  const [paidNote, setPaidNote] = useState<string | null>(null);

  const [selected, setSelected] = useState<ProviderSettlement | null>(null);
  // #634 — bumped whenever trips move in or out of the pool, so the unsettled list refetches.
  const [poolVersion, setPoolVersion] = useState(0);

  const load = useCallback(async () => {
    if (!auth.token) return;
    setLoading(true);
    setError(null);
    try {
      setSettlements(await providerSettlementsApi.list(
        { providerId: filterProvider || undefined, status: filterStatus }, auth.token));
    } catch (err) {
      setSettlements(null);
      setError(actionMessage(err, 'Failed to load provider settlements'));
    } finally {
      setLoading(false);
    }
  }, [auth.token, filterProvider, filterStatus]);

  const refreshAll = useCallback(async () => {
    setPoolVersion((v) => v + 1);
    await load();
  }, [load]);

  const loadProviders = useCallback(async () => {
    if (!auth.token) return;
    setRegistryError(null);
    try {
      setProviders(await deliveryConfigApi.listProviders(auth.token, { includeZofeur: true }));
    } catch (err) {
      setProviders([]);
      setRegistryError(actionMessage(err, 'Failed to load the recovery provider registry'));
    }
  }, [auth.token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadProviders(); }, [loadProviders]);

  // #634 — the providers still owed; refetched whenever trips move in or out of the pool.
  useEffect(() => {
    if (!auth.token) return;
    let cancelled = false;
    providerSettlementsApi.unsettledProviders(auth.token)
      .then((rows) => { if (!cancelled) setOwed(rows ?? []); })
      // Best effort: without it the picker still offers the active registry, as before #634.
      .catch(() => { if (!cancelled) setOwed([]); });
    return () => { cancelled = true; };
  }, [auth.token, poolVersion]);

  /**
   * Every active registry provider, plus every provider that still has unsettled trips whatever its
   * registry state — tagged "inactive" / "retired" so the admin knows why it is not offered for new
   * legs. "Retired" is only claimed when the registry actually loaded and does not list it.
   */
  const selectable = useMemo(() => {
    const byId = new Map(providers.map((p) => [p.id, p]));
    const count = new Map(owed.map((o) => [o.providerId, o.tripCount]));
    const options = new Map<string, { id: string; name: string; label: string }>();
    const label = (name: string, tag: string | null, id: string) => {
      const n = count.get(id);
      return `${name}${tag ? ` (${tag})` : ''}${n ? ` — ${n} unsettled` : ''}`;
    };
    providers.filter((p) => p.active).forEach((p) => {
      options.set(p.id, { id: p.id, name: p.name, label: label(p.name, null, p.id) });
    });
    owed.forEach((o) => {
      if (options.has(o.providerId)) return;
      const reg = byId.get(o.providerId);
      const name = reg?.name ?? o.providerName ?? 'Unnamed provider';
      const tag = reg ? 'inactive' : registryError ? null : 'retired';
      options.set(o.providerId, { id: o.providerId, name, label: label(name, tag, o.providerId) });
    });
    return Array.from(options.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [providers, owed, registryError]);

  /**
   * Filter options: the registry when it loaded, otherwise the providers the settlements themselves
   * name — so filtering still works when the registry is out of reach.
   */
  const filterOptions = useMemo(() => {
    if (providers.length > 0) return providers.map((p) => ({ id: p.id, name: p.name }));
    const seen = new Map<string, string>();
    (settlements ?? []).forEach((s) => seen.set(s.providerId, s.providerName));
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [providers, settlements]);

  // The period the pay panel shows: a whole Dubai month, or every unpaid trip up to a date.
  const period = useMemo(
    () => (periodMode === 'month' && month ? monthRange(month) : { to: until || dubaiToday() }),
    [periodMode, month, until],
  );
  const periodLabel = periodMode === 'month' && month
    ? monthLabel(month)
    : `up to ${formatTripDate(`${until || dubaiToday()}T12:00:00+04:00`)}`;

  const onPaid = async (s: ProviderSettlement) => {
    setPaidNote(
      `Paid ${formatAed(s.paymentAmount ?? 0)} to ${s.providerName} for ${s.tripCount} trip${s.tripCount === 1 ? '' : 's'} (${s.settlementNumber})`
      + (s.invoiceUploaded ? '.' : ' — attach the invoice from the settlement when you have it.'),
    );
    await refreshAll();
  };

  return (
    <div>
      <div className="admin-card" style={{ marginBottom: '1rem' }}>
        <div className="admin-card-header">
          <div>
            <h3 className="admin-card-title">Pay a provider</h3>
            <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
              Pick a provider and a period to see the unpaid trips, then mark them as paid.
            </div>
          </div>
        </div>

        {registryError && (
          <div className="admin-info-banner info" role="status" data-testid="registry-unavailable"
            style={{ marginBottom: '0.75rem' }}>
            The recovery provider registry could not be loaded, so only providers that already have
            unsettled trips can be picked: {registryError}. It lives on the Finance screen — an admin
            role without Finance cannot read it.
          </div>
        )}
        {(!registryError || selectable.length > 0) && (
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label htmlFor="pay-provider" style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>Provider</label>
            <select
              id="pay-provider"
              className="admin-select"
              value={payProvider}
              disabled={selectable.length === 0}
              onChange={(e) => { setPayProvider(e.target.value); setPaidNote(null); }}
            >
              <option value="">Select a provider…</option>
              {selectable.map((p) => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
            <label htmlFor="pay-period-mode" style={{ fontSize: '0.8125rem', color: 'var(--admin-text-muted)' }}>Period</label>
            <select id="pay-period-mode" className="admin-select" value={periodMode}
              onChange={(e) => { setPeriodMode(e.target.value as 'month' | 'until'); setPaidNote(null); }}>
              <option value="month">Month</option>
              <option value="until">Up to a date</option>
            </select>
            {periodMode === 'month' ? (
              <input id="pay-period-month" aria-label="Month" type="month" className="form-input" style={{ width: 'auto' }}
                value={month} max={dubaiToday().slice(0, 7)}
                onChange={(e) => { setMonth(e.target.value); setPaidNote(null); }} />
            ) : (
              <input id="pay-period-until" aria-label="Until" type="date" className="form-input" style={{ width: 'auto' }}
                value={until} onChange={(e) => { setUntil(e.target.value); setPaidNote(null); }} />
            )}
          </div>
        )}

        {paidNote && (
          <div className="admin-info-banner success" role="status" data-testid="paid-note" style={{ marginTop: '0.75rem' }}>
            {paidNote}
          </div>
        )}

        {payProvider && (
          <UnsettledTripsPanel
            providerId={payProvider}
            provider={providers.find((p) => p.id === payProvider) ?? null}
            period={period}
            periodLabel={periodLabel}
            refreshKey={poolVersion}
            onPaid={onPaid}
          />
        )}
      </div>

      <div className="admin-card">
        <div className="admin-card-header" style={{ flexWrap: 'wrap', gap: '0.75rem' }}>
          <h3 className="admin-card-title">Provider settlements</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.8125rem' }}
            role="group" aria-label="Provider settlement filters">
            <label htmlFor="filter-provider" style={{ color: 'var(--admin-text-muted)' }}>Provider</label>
            <select id="filter-provider" className="admin-select" value={filterProvider} disabled={loading}
              onChange={(e) => setFilterProvider(e.target.value)}>
              <option value="">All providers</option>
              {filterOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <label htmlFor="filter-status" style={{ color: 'var(--admin-text-muted)' }}>Status</label>
            <select id="filter-status" className="admin-select" value={filterStatus} disabled={loading}
              onChange={(e) => setFilterStatus(e.target.value as ProviderSettlementStatus | '')}>
              <option value="">All statuses</option>
              {PROVIDER_SETTLEMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {error && <div className="admin-info-banner danger" role="alert" data-testid="providers-error">{error}</div>}

        {loading && !settlements ? (
          <div className="admin-loading" data-testid="providers-loading"><div className="admin-loading-spinner"></div></div>
        ) : !settlements || settlements.length === 0 ? (
          <div className="admin-empty-state" data-testid="providers-empty">
            <p>No provider settlements yet. Pay one above once a provider has completed trips.</p>
          </div>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table" data-testid="provider-settlements-table">
              <thead>
                <tr>
                  <th>Settlement</th>
                  <th>Provider</th>
                  <th>Period</th>
                  <th style={{ textAlign: 'right' }}>Trips<InfoTip glossaryKey="providerSettlementTripCount" /></th>
                  <th style={{ textAlign: 'right' }}>Amount<InfoTip glossaryKey="providerSettlementAmount" /></th>
                  <th style={{ textAlign: 'right' }}>VAT<InfoTip glossaryKey="providerSettlementVatClaimed" /></th>
                  <th>Invoice</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {settlements.map((s) => (
                  <tr key={s.id} data-testid="provider-settlement-row">
                    {/* #580 — the number an admin matches against the bank statement, on the edge they scan. */}
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{s.settlementNumber}</td>
                    <td style={{ fontWeight: 500 }}>{s.providerName}</td>
                    <td>{formatPeriod(s.periodStart, s.periodEnd)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <Money amount={s.tripCount} glossaryKey="providerSettlementTripCount" format="count" hideTip />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <SettlementAmount settlement={s} hideTip />
                    </td>
                    <td style={{ textAlign: 'right' }} data-testid="provider-settlement-vat">
                      <Money amount={s.vatAmount} glossaryKey="providerSettlementVatClaimed" hideTip />
                    </td>
                    <td data-testid="provider-settlement-invoice">
                      {String(s.status).toUpperCase() !== 'PAID' ? '—'
                        : s.invoiceUploaded ? 'Uploaded'
                          : <span style={{ color: 'var(--admin-warning, #b45309)', fontWeight: 500 }}>Missing</span>}
                    </td>
                    <td><ProviderSettlementStatusBadge status={s.status} /></td>
                    <td>
                      <button type="button" className="admin-link" style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                        onClick={() => setSelected(s)}>
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ProviderSettlementDetail
        settlement={selected}
        onClose={() => setSelected(null)}
        onChanged={refreshAll}
      />
    </div>
  );
}
